/**
 * Modifier groups as a published menu carries them, and what a choice costs.
 *
 * Specification: ../test/menu-choices.spec.ts.
 *
 * The published form is deliberately the simple core of `./modifiers`: flat
 * groups, a fixed price delta per option. Validation and pricing are that
 * library's, so the till, the guest's phone and the server agree by construction.
 * Pure: the till runs this offline.
 */

import type { Translations } from "@gf/contracts/values";
import type { LineModifier } from "@gf/contracts/values";
import {
  priceModifiers,
  validateModifierChoices,
  type ModifierChoice,
  type ModifierGroup,
  type ModifierViolation,
  type Variant,
} from "./modifiers";

export interface ModifierOptionSnapshot {
  readonly optionId: string;
  readonly name: string;
  /** Gross, per unit of the line. Negative for "bez přílohy". */
  readonly priceDeltaMinor: bigint;
  /** At least 1. */
  readonly maxQuantity: number;
  /** Its name in other languages, for a guest reading the menu in theirs. */
  readonly translations?: Translations;
}

export interface ModifierGroupSnapshot {
  readonly groupId: string;
  readonly name: string;
  /** Above zero makes the choice required: "Příloha", "Propečení". */
  readonly minSelections: number;
  /** Null is unlimited. */
  readonly maxSelections: number | null;
  readonly options: readonly ModifierOptionSnapshot[];
  readonly translations?: Translations;
}

export interface CategorySnapshot {
  readonly categoryId: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly translations?: Translations;
}

/** One pick, as a screen produces it. */
export interface MenuChoice {
  readonly groupId: string;
  readonly optionId: string;
  /** Default 1. */
  readonly quantity?: number;
}

export type ChoiceResult =
  | {
      readonly ok: true;
      /** In the order the groups are offered, ready to freeze onto the line. */
      readonly modifiers: readonly LineModifier[];
      /** Sum of the deltas, per unit of the line. */
      readonly unitDeltaMinor: bigint;
    }
  | { readonly ok: false; readonly violation: ModifierViolation };

// The library prices by variant for size-keyed rules; a published menu has only
// flat prices, so any variant answers the same.
const FLAT: Variant = { sku: "flat", axisValues: {}, priceGrossMinor: 0n, taxCategory: "FOOD" };

const toLibrary = (groups: readonly ModifierGroupSnapshot[]): ModifierGroup[] =>
  groups.map((g) => ({
    id: g.groupId,
    name: g.name,
    minSelections: g.minSelections,
    maxSelections: g.maxSelections,
    options: g.options.map((o) => ({
      id: o.optionId,
      name: o.name,
      maxQuantity: o.maxQuantity,
      priceRule: { kind: "FLAT" as const, priceGrossMinor: o.priceDeltaMinor },
    })),
  }));

/**
 * Validate and price a server's picks against the groups an item offers.
 *
 * Refuses rather than repairs: a required group left empty, an option that is
 * not on the menu, one picked twice. The first violation is returned, because a
 * screen can only show one message at a time.
 */
export function resolveChoices(
  groups: readonly ModifierGroupSnapshot[],
  choices: readonly MenuChoice[],
): ChoiceResult {
  const library = toLibrary(groups);
  const picks: ModifierChoice[] = choices.map((c) => ({
    groupId: c.groupId,
    optionId: c.optionId,
    ...(c.quantity !== undefined ? { quantity: c.quantity } : {}),
  }));

  const [violation] = validateModifierChoices(library, picks);
  if (violation) return { ok: false, violation };

  // Group order, then option order — the order the menu offers them in, so a
  // ticket reads the same whichever the server tapped first.
  const rank = (c: ModifierChoice): number => {
    const g = groups.findIndex((x) => x.groupId === c.groupId);
    const o = groups[g]!.options.findIndex((x) => x.optionId === c.optionId);
    return g * 10_000 + o;
  };
  const ordered = [...picks].sort((a, b) => rank(a) - rank(b));

  const priced = priceModifiers(library, ordered, FLAT);
  const modifiers: LineModifier[] = priced.map((p) => ({
    groupId: p.groupId,
    optionId: p.optionId,
    name: p.name,
    quantity: p.quantity,
    priceDeltaMinor: p.priceDeltaGrossMinor,
  }));
  return {
    ok: true,
    modifiers,
    unitDeltaMinor: modifiers.reduce((sum, m) => sum + m.priceDeltaMinor, 0n),
  };
}

/** Whether ringing this item needs the modifier sheet before anything else. */
export const needsChoices = (groups: readonly ModifierGroupSnapshot[]): boolean =>
  groups.some((g) => g.minSelections > 0);
